import type { EconSectorCategory, EconSectorResponse } from '../api/chartDataTypes';
import { buildEconSectorSummarySentence } from '../utils/econSectorFormat';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { useSubmittedSelection } from '../utils/useSubmittedSelection';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { SECTOR_SUBMIT_GUIDANCE, SectorSelector } from './SectorSelector';

type EconSectorChartPanelProps = {
    response: EconSectorResponse;
};

const PROMPT_SUMMARY = 'Select a sector and press Submit to display its weighting.';
const EMPTY_SUMMARY = 'No sector data available.';

/**
 * Economic Sector Representation orchestrator. Unlike Perf10k / ShoutTna there
 * is no value to summarize on first render — the data is a point-in-time
 * snapshot keyed by sector name, not date. The shell's banner therefore opens
 * with a prompt; the user tabs from the header into the Sector dropdown and
 * presses Submit to display each category's weighting.
 */
export function EconSectorChartPanel({ response }: EconSectorChartPanelProps) {
    const { security, title, report_date, data } = response;
    const { categories } = data;

    const [selectedCategory, announcementKey, handleSelectedCategoryChange] =
        useSubmittedSelection<EconSectorCategory>();

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const hasCategories = categories.length > 0;

    let summary: string;
    let summaryHTML: string | undefined;
    if (!hasCategories) {
        summary = EMPTY_SUMMARY;
    } else if (selectedCategory === null) {
        summary = PROMPT_SUMMARY;
    } else {
        const parts = buildEconSectorSummarySentence(selectedCategory);
        summary = parts.text;
        summaryHTML = parts.html;
    }

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}
            announcementKey={announcementKey}
            guidance={SECTOR_SUBMIT_GUIDANCE}>
            {hasCategories ? (
                <SectorSelector
                    categories={categories}
                    idPrefix='econ_sector'
                    onSelectedCategoryChange={handleSelectedCategoryChange}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
